<script>
/*
  Vision Venture — News Card
  - Visual reference: user-provided screenshot (2026-08-05)
  - รักษา item/dataTest/dateFormat/select contract จาก CrmNewsCard
  - ใช้ API image/content เดิมและแยก VV styling เพื่อไม่กระทบ Standard V2
*/
export default {
  name: "VvNewsCard",
  props: {
    item: {
      type: Object,
      required: true
    },
    dataTest: {
      type: String,
      default: "news-card"
    },
    dateFormat: {
      type: String,
      default: "DD MMM YY"
    }
  },
  emits: ["select"],
  computed: {
    title() {
      return this.item.news_language?.[0]?.name || this.item.name || "-";
    },
    description() {
      const value =
        this.item.news_language?.[0]?.short_description ||
        this.item.news_language?.[0]?.description ||
        this.item.short_description ||
        this.item.description ||
        "";

      return String(value)
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    createdDate() {
      if (!this.item.created_date) return "";
      return this.$dateFormat(this.item.created_date, this.dateFormat);
    },
    imageSrc() {
      return this.item.image_url || this.handleImageSrc;
    }
  },
  methods: {
    selectCard() {
      this.$emit("select", this.item);
    }
  }
};
</script>

<template>
  <button
    type="button"
    class="vv-news-card"
    :data-test="dataTest"
    @click="selectCard"
  >
    <span class="vv-news-card__image-wrap" aria-hidden="true">
      <c-img-lazy :src="imageSrc" class="vv-news-card__image" />
    </span>

    <span class="vv-news-card__content">
      <span class="vv-news-card__copy">
        <strong
          :class="[
            'vv-news-card__title',
            { 'vv-news-card__title--expanded': !description }
          ]"
        >
          {{ title }}
        </strong>
        <span v-if="description" class="vv-news-card__description">
          {{ description }}
        </span>
      </span>

      <span class="vv-news-card__meta">
        <span v-if="createdDate" class="vv-news-card__date">
          {{ $t("generic_date") }} {{ createdDate }}
        </span>
        <span class="vv-news-card__action">
          {{ $t("discover") }}
          <font-awesome-icon icon="chevron-right" aria-hidden="true" />
        </span>
      </span>
    </span>
  </button>
</template>

<style lang="scss" scoped>
.vv-news-card {
  display: grid;
  width: 100%;
  min-height: 124px;
  grid-template-columns: 35% minmax(0, 1fr);
  overflow: hidden;
  padding: 0;
  border: var(--crm-card-glass-border);
  border-radius: var(--crm-border-radius-lg);
  background: var(--crm-card-glass-background);
  box-shadow: var(--crm-vv-profile-card-shadow);
  color: var(--text-color);
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-backdrop-filter: var(--crm-card-glass-backdrop-filter);
  backdrop-filter: var(--crm-card-glass-backdrop-filter);
  transition: box-shadow 150ms ease-out, transform 150ms ease-out;
}

.vv-news-card:active {
  transform: scale(0.99);
}

.vv-news-card:focus-visible {
  outline: 2px solid var(--primary-color);
  outline-offset: 2px;
}

.vv-news-card__image-wrap {
  display: block;
  min-width: 0;
  min-height: 124px;
  overflow: hidden;
  background: var(--secondary-color);
}

.vv-news-card__image,
.vv-news-card__image :deep(.v-img) {
  width: 100%;
  height: 100%;
}

.vv-news-card__image :deep(.v-img__img) {
  object-fit: cover !important;
}

.vv-news-card__content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 10px 12px 9px;
}

.vv-news-card__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.vv-news-card__title,
.vv-news-card__description {
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  text-wrap: pretty;
}

.vv-news-card__title {
  color: var(--text-color);
  font-size: var(--font-body-lg);
  font-weight: 700;
  line-height: 1.3;
  -webkit-line-clamp: 1;
}

.vv-news-card__title--expanded {
  -webkit-line-clamp: 2;
}

.vv-news-card__description {
  color: var(--text-color);
  font-size: var(--font-body);
  font-weight: 600;
  line-height: 1.35;
  -webkit-line-clamp: 2;
}

.vv-news-card__meta {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  color: var(--tab-text-color);
  font-size: var(--font-body-sm);
  line-height: 1.3;
}

.vv-news-card__date {
  overflow: hidden;
  min-width: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vv-news-card__action {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  white-space: nowrap;
}

.vv-news-card__action svg {
  font-size: var(--font-helper);
}

@media (prefers-reduced-motion: reduce) {
  .vv-news-card {
    transition: none;
  }
}
</style>
